import { useState, useRef, useEffect } from 'react';
import { client } from '../../sdkAdapter';

interface MediaUploaderProps {
    initialImages?: number[];
    multiple?: boolean;
    onUpload: (ids: number[]) => void;
    label?: string;
    folder?: string;
}

export default function MediaUploader({
    initialImages = [],
    multiple = false,
    onUpload,
    label = 'Upload Media',
    folder = 'products'
}: MediaUploaderProps) {

    const [loading, setLoading] = useState(false);
    const [previews, setPreviews] = useState<any[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const initialImagesKey = initialImages.join(',');

    // ✅ Fixed URL Builder
    const getFullUrl = (url?: string | null) => {
        if (!url) return null;

        if (url.startsWith('http')) return encodeURI(url);

        const baseUrl = (import.meta.env.VITE_API_BASE || 'http://localhost:8000')
            .replace(/\/$/, '');

        const cleanUrl = url.replace(/^\//, '');

        return encodeURI(`${baseUrl}/${cleanUrl}`);
    };


    // ✅ Fetch previews properly
    useEffect(() => {
        const fetchPreviews = async () => {
            if (!initialImages.length) {
                setPreviews([]);
                return;
            }

            try {
                const promises = initialImages.map((id) =>
                    client.getApiMediaFilesId({ id: String(id) })
                );

                const results = await Promise.all(promises);

                const files = results.flatMap((res: any) => res?.data?.data || []);

                setPreviews(files.filter(Boolean));

            } catch (err) {
                console.error('Failed to fetch previews', err);
            }
        };

        fetchPreviews();
    }, [initialImagesKey]);


    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            await uploadFiles(Array.from(e.target.files));
        }
    };

    const uploadFiles = async (files: File[]) => {
        setLoading(true);

        const newIds: number[] = [];
        const newFiles: any[] = [];

        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('folder', folder);

                const response = await client.postApiMediaFileUpload({
                    postApiMediaFileUploadRequest: formData as any
                }) as any;

                const uploadedFile = response.data?.data || response.data;

                if (uploadedFile?.id) {
                    newIds.push(uploadedFile.id);
                    newFiles.push(uploadedFile);
                }
            }

            if (multiple) {
                onUpload([...initialImages, ...newIds]);
            } else {
                onUpload(newIds);
            }

            // ✅ Instant preview update
            setPreviews(prev =>
                multiple ? [...prev, ...newFiles] : newFiles
            );

        } catch (err) {
            console.error('Upload failed', err);
            alert('Failed to upload image');
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemove = (idToRemove: number, e: React.MouseEvent) => {
        e.stopPropagation();

        const newIds = initialImages.filter(id => id !== idToRemove);
        onUpload(newIds);

        setPreviews(prev => prev.filter(p => p.id !== idToRemove));
    };

    return (
        <div className="space-y-3">

            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {label}
            </label>

            <div
                onClick={() => fileInputRef.current?.click()}
                className={`
                    relative border-2 border-dashed border-gray-300 
                    dark:border-gray-700 rounded-xl p-6
                    flex flex-col items-center justify-center 
                    cursor-pointer hover:border-brand-500
                    transition-all group
                    ${loading ? 'opacity-50 pointer-events-none' : ''}
                `}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileSelect}
                    multiple={multiple}
                    accept="image/*"
                />

                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {loading ? 'Uploading...' : 'Click to Upload'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                    {multiple ? 'Support multiple files' : 'Single file'}
                </p>
            </div>

            {/* ✅ PREVIEW SECTION */}
            {previews.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                    {previews.map((file) => {
                        const imageSrc =
                            getFullUrl(file.path) ||
                            getFullUrl(file.thumb_m) ||
                            getFullUrl(file.thumb_s);


                        if (!imageSrc) {
                        }

                        return (
                            <div key={file.id} className="relative w-24 h-24 group">

                                {imageSrc ? (
                                    <div>
                                        <img
                                            src={imageSrc}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                            onError={() => {
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemove(file.id, e)}
                                            className="absolute top-1 right-1 bg-red-500 text-white 
                                           rounded-full p-1 opacity-0 
                                           group-hover:opacity-100 transition"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center w-full h-full text-xs text-gray-400">
                                        No Image
                                    </div>
                                )}
                            </div>
                        );
                    })}

                </div>
            )}
        </div>
    );
}
